import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { CheckCircle, Banknote, Building2, Calendar, Mail, Phone, User, Car, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bank details for the company
const BANK_DETAILS = {
  bankName: 'Alpha Bank',
  accountHolder: 'Universal Rent a Car',
  iban: 'GR12 0140 1234 1234 0000 0000 123',
  bic: 'CRBAGRAA',
};

export default function BookingConfirmedPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const paymentMethod = searchParams.get('method') || 'cash';
  const { t } = useLanguage();
  
  const [booking, setBooking] = useState(null);
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${id}`);
        setBooking(response.data);
        
        // Fetch car details
        const carResponse = await axios.get(`${API}/cars/${response.data.car_id}`);
        setCar(carResponse.data);
      } catch (error) {
        console.error('Error fetching booking:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const copyIban = () => {
    navigator.clipboard.writeText(BANK_DETAILS.iban.replace(/\s/g, ''));
    setCopied(true);
    toast.success('IBAN copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="booking-confirmed-page">
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <div className="bg-card border border-border rounded-2xl p-8 md:p-12">
          {/* Success Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-success" />
            </div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-2">
              {t('bookingConfirmed')}
            </h1>
            <p className="text-muted-foreground">
              Booking ID: <span className="font-mono font-medium">{id?.slice(0, 8).toUpperCase()}</span>
            </p>
          </div>

          {/* Booking Details */}
          {booking && car && (
            <div className="bg-secondary rounded-xl p-6 mb-8">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-accent" />
                {car.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>{booking.customer_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{booking.customer_email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{booking.customer_phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>{booking.total_days} days</span>
                </div>
              </div>
              <div className="border-t border-border mt-4 pt-4">
                <div className="flex items-center gap-2 text-sm mb-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Pickup:</span>
                  <span className="font-medium">
                    {new Date(booking.pickup_date).toLocaleDateString()} at {booking.pickup_time}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Return:</span>
                  <span className="font-medium">
                    {new Date(booking.return_date).toLocaleDateString()} at {booking.return_time}
                  </span>
                </div>
              </div>
              <div className="border-t border-border mt-4 pt-4 flex justify-between items-center">
                <span className="font-semibold">{t('totalPrice')}</span>
                <span className="text-2xl font-bold text-gold">€{booking.total_price?.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Payment Instructions */}
          {paymentMethod === 'cash' && (
            <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold">{t('cashPayment')}</h3>
                  <p className="text-sm text-muted-foreground">{t('cashPaymentDesc')}</p>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <strong>Payment Instructions:</strong>
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Please bring the exact amount in cash (€{booking?.total_price?.toFixed(2)})</li>
                  <li>Payment is due at the pickup location</li>
                  <li>Bring your ID and driver's license</li>
                  <li>Contact us if you have any questions</li>
                </ul>
              </div>
            </div>
          )}

          {paymentMethod === 'bank' && (
            <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold">{t('bankTransfer')}</h3>
                  <p className="text-sm text-muted-foreground">{t('bankTransferDesc')}</p>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Bank</span>
                  <span className="font-medium">{BANK_DETAILS.bankName}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Account Holder</span>
                  <span className="font-medium">{BANK_DETAILS.accountHolder}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">IBAN</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{BANK_DETAILS.iban}</span>
                    <button onClick={copyIban} className="text-accent hover:text-accent/80">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">BIC/SWIFT</span>
                  <span className="font-mono text-sm">{BANK_DETAILS.bic}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">Reference</span>
                  <span className="font-mono text-sm font-medium">{id?.slice(0, 8).toUpperCase()}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                <strong>Important:</strong> Please include the booking reference in your transfer. 
                Your booking will be confirmed once we receive the payment.
              </p>
            </div>
          )}

          {/* Contact Info */}
          <div className="text-center mb-8">
            <p className="text-muted-foreground mb-2">Questions? Contact us:</p>
            <div className="flex justify-center gap-4">
              <a href="tel:+306972252180" className="text-accent hover:underline">
                +30 697 2252180
              </a>
              <span className="text-muted-foreground">|</span>
              <a href="tel:+306970095042" className="text-accent hover:underline">
                +30 6970095042
              </a>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button variant="outline">
                Back to Home
              </Button>
            </Link>
            <Link to="/fleet">
              <Button className="bg-accent hover:bg-accent/90">
                <Car className="w-4 h-4 mr-2" />
                Book Another Car
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
